import React, { forwardRef, useCallback, useEffect } from "react";

import { combineRefs, EMPTY_ARRAY, TAB_INDEX_FOCUSABLE_TAB_SEQUENCE } from "@multiweb/reactUtils";
import { ForwardRefType } from "@multiweb/types/react";
import { CaptionAlignment, CheckBoxComponent } from "../CheckBoxComponent";
import { CheckBoxFake } from "../CheckBoxFake";
import { ControlValueWrapper } from "../ControlValueWrapper";
import { FormComponentPropsWrapper } from "../FormComponentPropsWrapper";
import { LocalizationLoader } from "../LocalizationLoader";
import { LocalizationProvider } from "../LocalizationProvider";
import { MultiEditWrapper, MultiEditWrapperFakeComponentRendererProps } from "../MultiEditWrapper";
import { CheckBoxControlDataAttributesProps, DataAttributesProps, EditableControlComponentProps } from "../types";
import { ValidationErrorWrapper } from "../ValidationErrorWrapper";

export type CheckBoxProps = Omit<EditableControlComponentProps, "ref">
    & Pick<DataAttributesProps, "displayFormat" | "captionLocKey">
    & Pick<CheckBoxControlDataAttributesProps, "alignment">;

/** Možné formáty zobrazení CheckBoxu */
export enum CheckBoxDisplayFormat {
    twoState = "2s",
    threeState = "3s",
}

export const CheckBox: ForwardRefType<HTMLInputElement, CheckBoxProps> = forwardRef<HTMLInputElement, CheckBoxProps>(({
    id,
    dataKey,
    externalErrors,
    isInRequiredInvalidState,
    displayFormat,
    onChange: onChangeExternal,
    onValidityChanged,
    onValidationCallback,
    onMount,
    onBlur,
    onFocus,
    onMouseEnter,
    onMouseLeave,
    onMouseOver,
    onKeyDown,
    onClick,
    triggerValueChange,
    captionLocKey,
    alignment = CaptionAlignment.rightJustify,
    locKey,
    encrypted,
    multiEditActive,
    touched,
    enabledVisual,
    recommended,
    required,
    postponedRequired,
    isEditing,
}, ref) => {
    useEffect(() => {
        onMount && onMount({
            getDisplayedValue: (rawValue) => rawValue,
            getControlValue: (rawValue) => rawValue,
            convertControlValueToRawValue: (controlValue) => controlValue,
        });
    }, [onMount]);

    const fakeComponentRenderer = useCallback(({ disabled }: MultiEditWrapperFakeComponentRendererProps) => (
        <CheckBoxFake {...{ disabled, isEditing, locKey, onKeyDown, onClick, required, recommended, postponedRequired }} />
    ), [isEditing, locKey, onClick, onKeyDown, required, recommended, postponedRequired]);

    // Výchozí formát checkboxu je trojstavový, pokud není explicitně nastaven jako dvoustavový.
    // Dvoustavový checkbox se nastaví i v případě, že je checkbox required.
    const hasThreeStates = (displayFormat?.toLowerCase()) !== CheckBoxDisplayFormat.twoState && !required;

    const checkBox = (label?: string, title?: string) => (
        <ControlValueWrapper dataKey={dataKey}>
            {({ rawValue, onChange }) => (
                <ValidationErrorWrapper
                    {...{ dataKey, externalErrors, isInRequiredInvalidState, onValidityChanged }}
                    instantValidationFnDescriptions={EMPTY_ARRAY}
                    explicitValidationFnDescriptions={EMPTY_ARRAY}
                >
                    {({ invalid, validate }) => (
                        <FormComponentPropsWrapper {...{ postponedRequired, required, recommended, enabledVisual }} >
                            {({ disabled, backgroundColor, readOnly }) => (
                                <MultiEditWrapper {...{ multiEditActive, disabled, isEditing, touched, fakeComponentRenderer }}>
                                    {({ ref: checkBoxRef }) => {
                                        const joinedRefs = combineRefs<HTMLInputElement>(checkBoxRef, ref);

                                        return (
                                            <CheckBoxComponent
                                                {...{
                                                    id,
                                                    encrypted,
                                                    invalid,
                                                    onClick,
                                                    onBlur,
                                                    onFocus,
                                                    onMouseEnter,
                                                    onMouseLeave,
                                                    onMouseOver,
                                                    onKeyDown,
                                                    backgroundColor,
                                                    readOnly,
                                                    title,
                                                    disabled,
                                                    hasThreeStates,
                                                    label,
                                                    alignment,
                                                    required,
                                                }}
                                                value={rawValue}
                                                ref={joinedRefs}
                                                tabIndex={TAB_INDEX_FOCUSABLE_TAB_SEQUENCE}
                                                showFalseAsCrossed={true}
                                                forceDefaultValue={rawValue === undefined && !hasThreeStates && isEditing && touched && multiEditActive}
                                                onChange={(value) => {
                                                    onChange(value);
                                                    onChangeExternal && onChangeExternal(value);
                                                    validate(onValidationCallback);
                                                    triggerValueChange && triggerValueChange();
                                                }}
                                            />
                                        );
                                    }}
                                </MultiEditWrapper>
                            )}
                        </FormComponentPropsWrapper>
                    )}
                </ValidationErrorWrapper>
            )}
        </ControlValueWrapper>
    );

    return captionLocKey ? (
        <LocalizationLoader locKey={captionLocKey}>
            <LocalizationProvider locKey={captionLocKey}>
                {({ text, title }) => checkBox(text, title)}
            </LocalizationProvider>
        </LocalizationLoader>
    ) : (
        checkBox()
    );
});
